import { gql, useApolloClient } from '@apollo/client';
import { TextButton, Toast, useToast } from '@zillow/constellation-10';
import { useContext } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import type { SatelliteWebLandlordGetLatestConversationsForInboxResponseDto } from '@zg-rentals/ts-satellite-client';
import {
  MultiBrandSupportUserReportConversationReasonType,
  SatelliteConversationFolderTypeV2,
  SatelliteConversationMessageType,
} from '@zg-rentals/ts-satellite-client';
import { getCurrentSessionURL } from '../utils/fullStory';
import {
  useSatelliteWebLandlordConversationHandler_addSystemMessage,
  useSatelliteWebLandlordConversationHandler_archiveConversation,
  useSatelliteWebLandlordConversationHandler_markConversationAsRead,
  useSatelliteWebLandlordConversationHandler_markConversationAsUnread,
  useSatelliteWebLandlordConversationHandler_reportConversation,
  useSatelliteWebLandlordConversationHandler_unarchiveConversation,
  useSatelliteWebLandlordConversationHandler_updateConversationAttributes,
} from '@zg-rentals/ts-satellite-client-react-query';
import { ReportConversationReason } from '../types/GraphqlTypes';
import { preventMarkAsReadStore } from '../lib/inboxStores';
import { AppContext } from '../contexts/AppContextProvider';
import { InboxPageContext } from '../contexts/InboxPageContextProvider';
import useConversationListHelpers from './useConversationListHelpers';
import useInboxRouterHelper from './useInboxRouterHelper';
import useModal from './useModal';
import { reportReasonClickstreamEventMap } from '../constants/clickstreamEvents/reportProblemModal';
import { TRIGGER_LOCATION } from '../constants/clickstreamEvents/globals';
import { appMarkerLogger } from '../bootstrap';
import {
  emitIreneArchiveMessageEvent,
  emitIreneMarksMessageUnreadEvent,
  emitIreneUnarchiveMessageEvent,
} from '../constants/clickstreamEvents/conversationActions';
import { useAuth } from '../AuthProvider';
import { INBOX_CONVERSATIONS_TAG, type InboxConversationsInfiniteData } from './useInboxConversationsQuery';
import { getConversationMessagesQueryKey } from './useConversationMessagesQuery';
import type { ConversationMessagesData } from '../utils/conversationMessageUtils';

// Fields carried through the toast Undo flow so the undo entry point knows which folder
// the conv currently lives in and which folder it came from — needed to resolve the undo's
// own sourceFolder/destFolder (e.g., re-archive needs to know the conv is currently in INBOX
// and originally came from FAVORITE).
type FolderTransitionArgs = {
  // Folder the conv currently lives in — set by the primary action's `onUndoClick` to its
  // resolved `destFolder`, so the undo's source-side optimistic remove targets the right cache.
  currentFolder?: SatelliteConversationFolderTypeV2;
  // Folder the conv was in before the primary action — set by the primary's `onUndoClick`
  // to its resolved `sourceFolder`, so the undo restores the conv to its original folder
  // (e.g., FAVORITE if archive was triggered from the favorites view).
  originatingFolder?: SatelliteConversationFolderTypeV2;
};

type ConversationActionArgs = {
  conversationIdToUpdate?: string;
  listingAliasToUpdate?: string;
  retry?: boolean;
  isUndoAction?: boolean;
} & FolderTransitionArgs;

type ReportConversationArgs = {
  conversationIdToUpdate: string;
  reportReasonType: ReportConversationReason;
  userReportComment?: string;
  isUndoAction?: boolean;
} & FolderTransitionArgs;

// Resolved folders captured by the primary action and forwarded through the toast Undo
// handler so the undo entry point can plumb them back into its own sourceFolder/destFolder
// resolution.
type UndoClickPayload = {
  currentFolder: SatelliteConversationFolderTypeV2;
  originatingFolder: SatelliteConversationFolderTypeV2;
};

type GetUpdateConversationFunctionArgs = {
  csEvent?: () => void;
  toastBodySuccessText: string;
  toastBodyErrorText: string;
  performMutation: (conversationId: string) => Promise<void>;
  onUndoClick(payload: UndoClickPayload): void;
  // Folder the conv is moving _out_ of. Used by `removeConversationFromList` to do the
  // source-side optimistic remove (instant disappearance from the user's visible list).
  // Resolved by each entry point from either the user's current folder (primary) or
  // `args.currentFolder` (undo).
  sourceFolder: SatelliteConversationFolderTypeV2;
  // Folder the conv is moving _into_. Not used for cache surgery — the post-mutation
  // `invalidateQueries` refetch is the source of truth for destination state (sort order,
  // BE drift). This is only forwarded to the toast Undo handler so the undo can use it
  // as its own `currentFolder`.
  destFolder: SatelliteConversationFolderTypeV2;
};
const TOAST_TEXT_ARCHIVE = 'Conversation archived';
const TOAST_TEXT_REPORTED = 'Conversation reported';
const TOAST_TEXT_MOVED_TO_INBOX = 'Conversation moved to inbox';
const TOAST_TEXT_UNREAD = 'Conversation marked as unread';

const logger = appMarkerLogger.child({ name: 'useConversationActions' });

/**
 *
 * @description useConversationActions offers helpers to perform the following actions on a conversation
 * - Archive
 * - Report (spam)
 * - Move to inbox (from archive or reported (spam))
 * - Mark as read
 * - Mark as unread
 */
export default function () {
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const { changeConversation } = useInboxRouterHelper();
  const { getConversationToRedirectToNext, removeConversationFromList } = useConversationListHelpers();
  const { user } = useAuth();
  const { enqueueToast } = useToast();
  const { clearModal } = useModal();
  const fullStorySessionUrl = getCurrentSessionURL();
  const {
    conversationId,
    daysBack,
    listingAlias,
    folder,
    renterProgressStatus,
    readRepliedStatus,
    isByProperty,
    clientTimeZone,
  } = useContext(InboxPageContext);
  const { isImpersonator } = useContext(AppContext);

  const updateConversationInCache = (
    targetConversationId: string,
    updater: (
      conv: SatelliteWebLandlordGetLatestConversationsForInboxResponseDto['conversations'][number],
    ) => SatelliteWebLandlordGetLatestConversationsForInboxResponseDto['conversations'][number],
  ) => {
    queryClient.setQueriesData<InboxConversationsInfiniteData>(
      {
        predicate: (query) => {
          const tags = (query.meta as { tags?: Array<string> })?.tags;
          return tags?.includes(INBOX_CONVERSATIONS_TAG) ?? false;
        },
      },
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            conversations: page.conversations.map((conv) =>
              conv.conversationId === targetConversationId ? updater(conv) : conv,
            ),
          })),
        };
      },
    );
  };

  /**
   * Mirror a `hasUnreadMessage` toggle into the React Query conversation-messages cache
   * for the active conversation. Without this, `MessageList`/`MessageListItems` read
   * `hasUnreadMessage` from the RQ cache and would keep firing mark-read/unread calls
   * (or showing the new-message banner) until the next poll refreshes the cache — and
   * with websockets enabled there is no poll at all, so the staleness is permanent.
   */
  const setMessagesCacheHasUnreadMessage = (targetConversationId: string, hasUnreadMessage: boolean) => {
    queryClient.setQueryData<ConversationMessagesData>(
      getConversationMessagesQueryKey({ conversationId: targetConversationId, clientTimeZone }),
      (existing) => (existing ? { ...existing, hasUnreadMessage } : existing),
    );
  };

  /**
   * Mirror an archived/spam `status` change into the React Query conversation-messages cache for
   * the target conversation. `ConversationNav` (via `ConversationActions`) and `MessageListActions`
   * read `status` from this cache; without this mirror the action stays stale until the next poll —
   * and with websockets enabled there is no poll — so e.g. re-opening a just-archived conversation
   * would still render the "Archive" menu item instead of the "Unarchive" button.
   */
  const setMessagesCacheStatus = (
    targetConversationId: string,
    statusUpdate: Partial<ConversationMessagesData['status']>,
  ) => {
    queryClient.setQueryData<ConversationMessagesData>(
      getConversationMessagesQueryKey({ conversationId: targetConversationId, clientTimeZone }),
      (existing) => (existing ? { ...existing, status: { ...existing.status, ...statusUpdate } } : existing),
    );
  };

  const { mutateAsync: archiveConversationMutate } = useSatelliteWebLandlordConversationHandler_archiveConversation({
    meta: {
      invalidates: ['satellite:conversationCounts'],
      context: {
        api: 'archiveConversation',
        details: 'useConversationActions - archiving conversation',
      },
    },
  });

  const { mutateAsync: unarchiveConversationMutate } = useSatelliteWebLandlordConversationHandler_unarchiveConversation(
    {
      meta: {
        invalidates: ['satellite:conversationCounts'],
        context: {
          api: 'unarchiveConversation',
          details: 'useConversationActions - unarchiving conversation',
        },
      },
    },
  );

  const { mutateAsync: reportConversationMutate } = useSatelliteWebLandlordConversationHandler_reportConversation({
    meta: {
      invalidates: ['satellite:conversationCounts'],
      context: {
        api: 'reportConversation',
        details: 'useConversationActions - reporting conversation',
      },
    },
  });

  const { mutate: markAsUnread } = useSatelliteWebLandlordConversationHandler_markConversationAsUnread({
    meta: {
      invalidates: ['satellite:conversationCounts'],
      context: {
        api: 'markConversationAsUnread',
        details: 'useConversationActions - marking conversation as unread',
      },
    },
  });

  const { mutateAsync: markConversationAsReadMutate } =
    useSatelliteWebLandlordConversationHandler_markConversationAsRead({
      meta: {
        invalidates: ['satellite:conversationCounts'],
        context: {
          api: 'markConversationAsRead',
          details: 'useConversationActions - marking conversation as read',
        },
      },
    });

  const { mutateAsync: updateConversationAttributes } =
    useSatelliteWebLandlordConversationHandler_updateConversationAttributes({
      meta: {
        context: {
          api: 'updateConversationAttributes',
          details: 'useConversationActions - Error marking conversation as favorite or unfavorite',
        },
      },
    });

  const { mutate: addSystemMessage, error: addSystemMessageError } =
    useSatelliteWebLandlordConversationHandler_addSystemMessage({
      meta: {
        context: {
          api: 'addSystemMessage',
          details: 'useConversationActions - Error adding system message to conversation',
          fullStorySessionUrl,
        },
      },
    });

  function getUpdateConversationFunction({
    csEvent,
    toastBodySuccessText,
    toastBodyErrorText,
    performMutation,
    onUndoClick,
    sourceFolder,
    destFolder,
  }: GetUpdateConversationFunctionArgs): (args?: ConversationActionArgs) => Promise<void> {
    const updateConversation = async ({
      conversationIdToUpdate = conversationId,
      listingAliasToUpdate = listingAlias,
      retry = false,
      isUndoAction = false,
    }: ConversationActionArgs = {}): Promise<void> => {
      if (isImpersonator) return;

      if (!conversationIdToUpdate) {
        logger.warn('No conversationId provided when trying to update conversation status');
        return;
      }

      csEvent?.();

      try {
        await performMutation(conversationIdToUpdate);

        // Source-side optimistic remove: instant disappearance from the user's current
        // folder list (or, for undo flows, from the folder the conv is currently in).
        const { conversations } = removeConversationFromList(conversationIdToUpdate, sourceFolder);

        if (isUndoAction) {
          // Undo paths invalidate so the active view (where the conv needs to reappear)
          // refetches from the BE. The optimistic remove from `sourceFolder` doesn't help
          // here — the conv is going BACK to `originatingFolder`, which is the user's
          // active view, and we don't have the conv data snapshot to optimistically add
          // it. Refetching is how it reappears. Race risk exists (BE write propagation
          // vs refetch) but matches pre-PR undo behavior, which is acceptable in practice.
          changeConversation({
            conversationId: conversationIdToUpdate,
            listingAlias: listingAliasToUpdate || '',
          });
          queryClient.invalidateQueries({
            predicate: (query) => {
              const tags = (query.meta as { tags?: Array<string> })?.tags;
              return tags?.includes(INBOX_CONVERSATIONS_TAG) ?? false;
            },
          });
        } else {
          // Primary actions trust the optimistic remove. Don't invalidate — refetching
          // the active view here would race BE write propagation and could put the conv
          // back into the source list (visible flicker). Destination folder caches are
          // already stale by default (staleTime: 0) and refetch on natural navigation.
          // Matches pre-PR primary behavior.
          const { nextConversationId, nextListingAlias } = getConversationToRedirectToNext(
            conversationId || '',
            conversations,
          );

          changeConversation({
            conversationId: nextConversationId,
            listingAlias: nextListingAlias,
          });
        }

        enqueueToast(
          <Toast
            tone="success"
            actionButton={
              !isUndoAction && (
                <TextButton
                  onClick={(event) => {
                    event.stopPropagation();
                    // Forward the resolved folders to the undo entry point so it can plumb
                    // them back into its own sourceFolder/destFolder resolution.
                    return onUndoClick({
                      currentFolder: destFolder,
                      originatingFolder: sourceFolder,
                    });
                  }}
                  onImpact
                >
                  Undo
                </TextButton>
              )
            }
          >
            {toastBodySuccessText}
          </Toast>,
        );
      } catch {
        if (!retry) {
          logger.info('retrying updating conversation');
          // Preserve `isUndoAction` on retry so the second attempt re-enters the same branch
          // (undo vs primary). `sourceFolder`/`destFolder` are captured in the outer closure
          // and don't need to be re-forwarded.
          return updateConversation({
            conversationIdToUpdate,
            listingAliasToUpdate,
            retry: true,
            isUndoAction,
          });
        }

        enqueueToast(<Toast tone="critical">{toastBodyErrorText}</Toast>);
      }
    };

    return updateConversation;
  }

  function reportConversation(args: ReportConversationArgs) {
    if (isImpersonator) return;

    const { reportReasonType, userReportComment, isUndoAction } = args;
    const reportReasonTypeMappingFn =
      reportReasonClickstreamEventMap[
        reportReasonType as Exclude<ReportConversationReason, ReportConversationReason.NotReported>
      ];

    // Only emit the "user reported with reason X" clickstream for primary report actions.
    // Undo replays (the toast Undo on a "Move to inbox from spam" flow) re-call this entry
    // point to put the conv back into spam, but no real "report" action happened — the user
    // is reversing an un-report.
    if (reportReasonTypeMappingFn && !isUndoAction) {
      reportReasonTypeMappingFn({ user, listing: { listingAlias }, triggerLocation: TRIGGER_LOCATION.IreneInbox });
    }

    // Primary action: conv lives in the user's current folder. Undo-as-redo: conv lives where
    // the previous unreport just put it (carried in `args.currentFolder`).
    const sourceFolder = args.isUndoAction ? (args.currentFolder ?? folder) : folder;
    // Primary action: conv goes to SPAM. Undo-as-redo: conv goes back where the previous
    // unreport had moved it from (its `originatingFolder`, defaulting to SPAM for safety).
    const destFolder = args.isUndoAction
      ? (args.originatingFolder ?? SatelliteConversationFolderTypeV2.SPAM)
      : SatelliteConversationFolderTypeV2.SPAM;

    return getUpdateConversationFunction({
      toastBodySuccessText: TOAST_TEXT_REPORTED,
      toastBodyErrorText: 'There was an error reporting this conversation',
      sourceFolder,
      destFolder,
      performMutation: async (convId) => {
        await reportConversationMutate({
          postData: {
            conversationId: convId,
            reportReasonType: reportReasonType as unknown as MultiBrandSupportUserReportConversationReasonType,
            reportUserComment: userReportComment,
          },
        });
        updateApolloConversationStatus(convId, { isSpam: true });
        setMessagesCacheStatus(convId, { isSpam: true });
      },
      onUndoClick: ({ currentFolder, originatingFolder }) => {
        undoReportConversation({
          ...args,
          isUndoAction: true,
          currentFolder,
          originatingFolder,
        });
      },
    })(args);
  }

  function undoReportConversation(args: ReportConversationArgs) {
    if (isImpersonator) return;

    // Primary "Move to inbox" from spam view: conv lives in SPAM, restore to INBOX.
    // Undo-of-report (toast Undo): conv lives in SPAM (just moved there), restore to wherever
    // the report was triggered from (`args.originatingFolder`).
    const sourceFolder = args.isUndoAction
      ? (args.currentFolder ?? SatelliteConversationFolderTypeV2.SPAM)
      : SatelliteConversationFolderTypeV2.SPAM;
    const destFolder = args.isUndoAction
      ? (args.originatingFolder ?? SatelliteConversationFolderTypeV2.INBOX)
      : SatelliteConversationFolderTypeV2.INBOX;

    return getUpdateConversationFunction({
      toastBodySuccessText: TOAST_TEXT_MOVED_TO_INBOX,
      toastBodyErrorText: 'There was an error moving conversation back to your inbox',
      sourceFolder,
      destFolder,
      performMutation: async (convId) => {
        await reportConversationMutate({
          postData: {
            conversationId: convId,
            reportReasonType: MultiBrandSupportUserReportConversationReasonType.NOT_REPORTED,
          },
        });
        updateApolloConversationStatus(convId, { isSpam: false });
        setMessagesCacheStatus(convId, { isSpam: false });
      },
      onUndoClick: ({ currentFolder, originatingFolder }) => {
        // Override `reportReasonType` for the redo: `undoReportConversation` is called with
        // `NotReported` for primary "Move to inbox from spam" flows, but the toast Undo needs
        // to re-report the conv so it actually goes back to SPAM in the backend. Default to
        // `Spam` since we don't snapshot the original report reason — if a future change
        // needs to preserve specific reasons (DISCRIMINATORY_OR_ABUSIVE, OTHER), thread them
        // through `FolderTransitionArgs` instead.
        reportConversation({
          ...args,
          reportReasonType: ReportConversationReason.Spam,
          isUndoAction: true,
          currentFolder,
          originatingFolder,
        });
      },
    })(args);
  }

  function updateApolloConversationStatus(convId: string, statusUpdate: Record<string, boolean>) {
    apolloClient.cache.modify({
      id: apolloClient.cache.identify({ __typename: 'ZRM_Conversation', conversationId: convId }),
      fields: {
        status(cachedStatus: Record<string, unknown>) {
          return { ...cachedStatus, ...statusUpdate };
        },
      },
    });
  }

  function archiveConversation(args?: ConversationActionArgs) {
    if (isImpersonator) return;

    // Primary archive: source = wherever the user is viewing (INBOX, FAVORITE, etc.).
    // Undo-as-redo (re-archive after Move-to-inbox): conv currently lives in INBOX (carried
    // through `args.currentFolder`); destination is the original folder (`args.originatingFolder`).
    const sourceFolder = args?.isUndoAction ? (args.currentFolder ?? folder) : folder;
    const destFolder = args?.isUndoAction
      ? (args.originatingFolder ?? SatelliteConversationFolderTypeV2.ARCHIVED)
      : SatelliteConversationFolderTypeV2.ARCHIVED;

    return getUpdateConversationFunction({
      csEvent: () => emitIreneArchiveMessageEvent(user),
      toastBodySuccessText: TOAST_TEXT_ARCHIVE,
      toastBodyErrorText: 'There was an error marking this conversation as archived',
      sourceFolder,
      destFolder,
      performMutation: async (convId) => {
        await archiveConversationMutate({ postData: { conversationId: convId } });
        updateApolloConversationStatus(convId, { isArchived: true });
        setMessagesCacheStatus(convId, { isArchived: true });
      },
      onUndoClick: ({ currentFolder, originatingFolder }) => {
        undoArchiveConversation({
          ...args,
          isUndoAction: true,
          currentFolder,
          originatingFolder,
        });
      },
    })(args);
  }

  function undoArchiveConversation(args?: ConversationActionArgs) {
    if (isImpersonator) return;

    // Primary "Move to inbox" from archived view: conv lives in ARCHIVED, restore to INBOX.
    // Undo-of-archive (toast Undo): conv lives in ARCHIVED (just moved there), restore to the
    // folder archive was triggered from (`args.originatingFolder` — could be FAVORITE if the
    // user archived from the favorites view).
    const sourceFolder = args?.isUndoAction
      ? (args.currentFolder ?? SatelliteConversationFolderTypeV2.ARCHIVED)
      : SatelliteConversationFolderTypeV2.ARCHIVED;
    const destFolder = args?.isUndoAction
      ? (args.originatingFolder ?? SatelliteConversationFolderTypeV2.INBOX)
      : SatelliteConversationFolderTypeV2.INBOX;

    return getUpdateConversationFunction({
      csEvent: () => emitIreneUnarchiveMessageEvent(user),
      toastBodySuccessText: TOAST_TEXT_MOVED_TO_INBOX,
      toastBodyErrorText: 'There was an error moving conversation back to your inbox',
      sourceFolder,
      destFolder,
      performMutation: async (convId) => {
        await unarchiveConversationMutate({ postData: { conversationId: convId } });
        updateApolloConversationStatus(convId, { isArchived: false });
        setMessagesCacheStatus(convId, { isArchived: false });
      },
      onUndoClick: ({ currentFolder, originatingFolder }) => {
        archiveConversation({
          ...args,
          isUndoAction: true,
          currentFolder,
          originatingFolder,
        });
      },
    })(args);
  }

  const markConversationAsUnread = ({
    conversationIdToUpdate = conversationId,
  }: {
    conversationIdToUpdate?: string;
  } = {}): void => {
    if (isImpersonator) return;

    if (!conversationIdToUpdate) {
      logger.warn('No conversationId provided when trying to mark conversation as unread');
      return;
    }

    emitIreneMarksMessageUnreadEvent(user);

    markAsUnread(
      { postData: { conversationId: conversationIdToUpdate } },
      {
        onSuccess: () => {
          preventMarkAsReadStore.set(true);

          apolloClient.cache.writeFragment({
            id: apolloClient.cache.identify({
              conversationId: conversationIdToUpdate,
              __typename: 'ZRM_Conversation',
            }),
            fragment: gql`
              fragment ReadStatus on ZRM_Conversation {
                hasUnreadMessage
              }
            `,
            data: {
              hasUnreadMessage: true,
            },
          });

          updateConversationInCache(conversationIdToUpdate, (conv) => ({ ...conv, hasUnreadMessage: true }));
          setMessagesCacheHasUnreadMessage(conversationIdToUpdate, true);

          enqueueToast(<Toast tone="success">{TOAST_TEXT_UNREAD}</Toast>);
        },
        onError: () => {
          enqueueToast(<Toast tone="critical">Conversation couldn’t be marked as unread. Try again.</Toast>);
        },
      },
    );
  };

  const markConversationAsRead = async (lastReadMs: string) => {
    if (isImpersonator) return;

    if (!conversationId) {
      // Should never happen, function should only be called when conversationId exists
      logger.warn('No conversationId provided when trying to mark conversation as read');
      return;
    }

    try {
      await markConversationAsReadMutate({
        postData: {
          conversationId,
          lastReadMs: Number(lastReadMs),
        },
      });

      apolloClient.cache.writeFragment({
        id: apolloClient.cache.identify({ conversationId, __typename: 'ZRM_Conversation' }),
        fragment: gql`
          fragment ReadStatus on ZRM_Conversation {
            hasUnreadMessage
          }
        `,
        data: {
          hasUnreadMessage: false,
        },
      });

      updateConversationInCache(conversationId, (conv) => ({ ...conv, hasUnreadMessage: false }));
      setMessagesCacheHasUnreadMessage(conversationId, false);
    } catch {
      // Error logged automatically via meta.context
    }
  };
  const markConversationAsFavoriteOrUnfavorite = async (conversationIdToUpdate: string, isFavorite: boolean) => {
    if (isImpersonator) return;

    await updateConversationAttributes({
      postData: {
        conversationId: conversationIdToUpdate,
        isFavorite: !isFavorite,
      },
    });

    apolloClient.cache.modify({
      id: apolloClient.cache.identify({
        __typename: 'ZRM_Conversation',
        conversationId: conversationIdToUpdate,
      }),
      fields: {
        status(cachedStatus: { isFavorite?: boolean }) {
          return {
            ...cachedStatus,
            isFavorite: !isFavorite,
          };
        },
      },
    });

    updateConversationInCache(conversationIdToUpdate, (conv) => ({
      ...conv,
      status: { ...conv.status, isFavorite: !isFavorite },
    }));

    if (isFavorite) {
      if (folder === SatelliteConversationFolderTypeV2.FAVORITE && conversationIdToUpdate === conversationId) {
        const { conversations } = removeConversationFromList(
          conversationIdToUpdate,
          SatelliteConversationFolderTypeV2.FAVORITE,
        );
        const { nextConversationId, nextListingAlias } = getConversationToRedirectToNext(
          conversationIdToUpdate,
          conversations,
        );

        changeConversation({
          conversationId: nextConversationId,
          listingAlias: nextListingAlias,
        });
      } else {
        removeConversationFromList(conversationIdToUpdate, SatelliteConversationFolderTypeV2.FAVORITE);
      }
    }
  };

  const requestRenterProfile = (overrides?: {
    conversationId?: string;
    listingAlias?: string;
    clientTimeZone?: string;
    onSuccess?: () => void;
  }) => {
    const targetConversationId = overrides?.conversationId ?? conversationId;
    const targetClientTimeZone = overrides?.clientTimeZone ?? clientTimeZone;

    if (isImpersonator || !targetConversationId) return;

    addSystemMessage(
      {
        postData: {
          conversationId: targetConversationId,
          messageType: SatelliteConversationMessageType.RENTER_PROFILE_REQUEST,
        },
      },
      {
        onSuccess: () => {
          clearModal();
          overrides?.onSuccess?.();
          enqueueToast(<Toast tone="success">Renter profile request sent</Toast>);

          // Refetch the conversation messages so the new "renter profile request" system
          // message appears immediately (rather than waiting for the next poll).
          queryClient.invalidateQueries({
            queryKey: getConversationMessagesQueryKey({
              conversationId: targetConversationId,
              clientTimeZone: targetClientTimeZone,
            }),
          });
        },
        onError: () => {
          enqueueToast(<Toast tone="critical">Renter profile request couldn’t be sent. Try again.</Toast>);
        },
      },
    );
  };

  return {
    archiveConversation,
    reportConversation,
    requestRenterProfile,
    undoArchiveConversation,
    undoReportConversation,
    markConversationAsUnread,
    markConversationAsRead,
    markConversationAsFavoriteOrUnfavorite,
    hasErrorOnRequestRenterProfile: Boolean(addSystemMessageError),
  };
}
